import CommentsWidget from './widgets/CommentsWidget';
import FaultyCarWidget from './widgets/FaultyCarWidget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import DashboardWidgetsWrapper from 'components/Dashboard/WidgetWrapper';
import { GoSupportWidgetsEnum } from './utils';
import { memo } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import SortableWidget from 'components/Dashboard/SortableWidget';
import { WidgetConfig } from 'components/Dashboard/types';
import BookedToEditedByFTWidget from './widgets/BookedToEditedByFTWidget';
import DeviationReportsWidget from './widgets/DeviationReportsWidget';
import NotAcceptedTomorrowsAssignmentsWidget from './widgets/NotAcceptedTomorrowsAssignmentsWidget';
import BookedToTimePassedWidget from './widgets/BookedToTimePassedWidget';
import EstimatedEndTimePassedWidget from './widgets/EstimatedEndTimePassedWidget';
import EstimatedStartTimePassedWidget from './widgets/EstimatedStartTimePassedWidget';
import WorkGearReportsWidget from './widgets/WorkGearReportsWidget';
import FlaggedForSpeedingWidget from './widgets/FlaggedForSpeedingWidget';
import IncompleteDayPlanWidget from './widgets/IncompleteDayPlanWidget';
import NotLoadedTyresWidget from './widgets/NotLoadedTyresWidget';
import AssignmentRescheduledToAfterMidnightWidget from './widgets/AssignmentRescheduledToAfterMidnightWidget';
import StandbyTimeAssignmentsWidget from './widgets/StandbyTimeAssignmentsWidget';

const widgetConfigGoSupport: WidgetConfig<GoSupportWidgetsEnum>[] = [
  {
    widgetType: GoSupportWidgetsEnum.FaultyCar,
    component: memo(FaultyCarWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.Comments,
    component: memo(CommentsWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.DeviationReports,
    component: memo(DeviationReportsWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.BookedToEditedByFT,
    component: memo(BookedToEditedByFTWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.NotAcceptedTomorrowsAssignments,
    component: memo(NotAcceptedTomorrowsAssignmentsWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.EstimatedStartTimePassed,
    component: memo(EstimatedStartTimePassedWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.EstimatedEndTimePassed,
    component: memo(EstimatedEndTimePassedWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.BookedToTimePassed,
    component: memo(BookedToTimePassedWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.WorkGearReports,
    component: memo(WorkGearReportsWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.FlaggedForSpeeding,
    component: memo(FlaggedForSpeedingWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.NotLoadedTyres,
    component: memo(NotLoadedTyresWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.IncompleteDayPlan,
    component: memo(IncompleteDayPlanWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.AssignmentRescheduledToAfterMidnight,
    component: memo(AssignmentRescheduledToAfterMidnightWidget),
  },
  {
    widgetType: GoSupportWidgetsEnum.StandbyTimeAssignments,
    component: memo(StandbyTimeAssignmentsWidget),
  },
];

const componentByWidgetType = new Map(
  widgetConfigGoSupport.map(({ widgetType, component }) => [
    widgetType,
    component,
  ])
);

// dnd-kit ids are strings here: Comments is enum value 0, and a falsy id is not safe to drag.
const toSortableId = (widgetType: GoSupportWidgetsEnum) => String(widgetType);

const DashboardGoSupport = () => {
  const {
    dashboardState: { widgets },
    setWidgets,
  } = useDashboardControls<GoSupportWidgetsEnum>();

  // A small distance keeps a click on the drag handle from counting as a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const visibleWidgets = widgets.filter(
    (w) => w.active && componentByWidgetType.has(w.identifier)
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    // Move within the full list, hidden cards included, so a hidden card keeps its place
    // relative to its neighbours when it is shown again.
    const oldIndex = widgets.findIndex(
      (w) => toSortableId(w.identifier) === active.id
    );
    const newIndex = widgets.findIndex(
      (w) => toSortableId(w.identifier) === over.id
    );
    if (oldIndex < 0 || newIndex < 0) return;

    setWidgets(arrayMove(widgets, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={visibleWidgets.map((w) => toSortableId(w.identifier))}
        strategy={rectSortingStrategy}
      >
        <DashboardWidgetsWrapper>
          {visibleWidgets.map(({ identifier }) => {
            const Component = componentByWidgetType.get(identifier)!;
            return (
              <SortableWidget key={identifier} id={toSortableId(identifier)}>
                <Component />
              </SortableWidget>
            );
          })}
        </DashboardWidgetsWrapper>
      </SortableContext>
    </DndContext>
  );
};

export default DashboardGoSupport;
